// src/components/services/SavingsAccount.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdCheckCircle,
    MdAccessTime,
    MdHandshake,
    MdOutlineStar,
    MdSecurity,
} from "react-icons/md";
import { FaPhoneAlt } from "react-icons/fa";

function SavingsAccount() {
    const serviceName = "Savings Accounts"; // Define serviceName

    // Animation Variants (Keep these here, or move to a separate file)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Savings Accounts
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        As a Business Correspondent of Fino Payments Bank, Keziah Global Private
                        Limited offers secure and accessible savings accounts. Earn
                        attractive interest rates while enjoying the flexibility of
                        managing your funds conveniently.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Features & Benefits
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdCheckCircle className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Competitive Interest Rates
                                    </h4>
                                    <p className="text-gray-600">
                                        Enjoy attractive interest rates that help your savings grow
                                        faster.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdAccessTime className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Easy Account Opening
                                    </h4>
                                    <p className="text-gray-600">
                                        Open an account quickly and conveniently with minimal
                                        paperwork.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdCheckCircle className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Online & Mobile Banking
                                    </h4>
                                    <p className="text-gray-600">
                                        Access your account 24/7 through Fino Payments Bank's secure
                                        online and mobile banking platforms.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdHandshake className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Personalized Service
                                    </h4>
                                    <p className="text-gray-600">
                                        Benefit from personalized assistance and support from our
                                        dedicated team at Keziah.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineStar className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        No Hidden Charges
                                    </h4>
                                    <p className="text-gray-600">
                                        Enjoy transparent banking with no hidden fees or charges on
                                        your savings account.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdSecurity className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Nomination Facility
                                    </h4>
                                    <p className="text-gray-600">
                                        Secure your family's future with our easy nomination
                                        facility.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Eligibility */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Eligibility
                        </h3>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg">
                            <li>Resident individuals of India</li>
                            <li>Minimum age of 18 years (or with a guardian for minors)</li>
                            <li>Valid KYC documents (proof of identity and address)</li>
                        </ul>
                    </motion.div>

                    {/* How to Apply */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            How to Apply
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                            Visit our branch in Salem, Tamil Nadu, or contact us via phone or
                            email to open a Savings Account. You can also request a callback,
                            and our representatives will be happy to assist you with the
                            process.
                        </p>
                    </motion.div>

                    {/* Call to Action Buttons */}
                    <motion.div
                        variants={itemVariants}
                        className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                    >
                        <motion.a
                            href="tel:9043588446"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                            whileHover={{ scale: 1.05 }}
                        >
                            <FaPhoneAlt className="mr-2" />
                            Call Now
                        </motion.a>
                        {/* Use Link instead of button and modal */}
                        <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                            <motion.button
                                className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                Request a Callback
                            </motion.button>
                        </Link>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default SavingsAccount;